import { useNavigate } from 'react-router-dom'

import { PerformanceProcessTargetEvaluationCardFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { EvaluationCardBase } from 'modules/performance_process/components/common/EvaluationCardBase'

export const EvaluationReviewerCard: React.FC<{
  evaluation: PerformanceProcessTargetEvaluationCardFragment
  reviewId: string
  targetId: string | undefined
  strategyReviewerAccessIds: string[]
  isCheckingOwnResults?: boolean
  showEvaluationScore?: boolean
}> = ({
  evaluation: {
    id,
    evaluationType,
    targetAccess,
    reviewerAccess,
    status,
    _policies,
    answeredQuestionnaires,
    answeredQuestionnairesIdentifier,
    evaluationScore,
    publishedAt,
    republishedAt,
    performanceReviewProcess,
  },
  reviewId,
  targetId,
  strategyReviewerAccessIds,
  isCheckingOwnResults = false,
  showEvaluationScore = false,
}) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const canReplaceReviewer = !!(_policies?.replaceReviewer.can && targetAccess && reviewerAccess)
  const visibleAnswers = !!answeredQuestionnairesIdentifier?.id

  const link = isCheckingOwnResults
    ? navigation.performanceProcess.reviews.review.myResultsEvaluation.fullPath({
        reviewId: reviewId,
        targetId: targetId ?? '',
        evaluationId: id,
      })
    : navigation.performanceProcess.reviews.review.target.evaluation.fullPath({
        reviewId: reviewId,
        targetId: targetId ?? '',
        evaluationId: id,
      })

  const handleCardClick = () => !!visibleAnswers && navigate(link)

  const canRepublish = answeredQuestionnaires?._policies?.republish?.can ?? false

  const handleRepublish = () => {
    navigate(link, { state: { isRepublishing: true } })
  }

  const excludedAccessIds: Set<string> = new Set(
    [reviewerAccess?.id, ...strategyReviewerAccessIds].filter(Boolean)
  )

  const reviewerName = reviewerAccess
    ? reviewerAccess.fullName
    : i18n.t('performance_process.reviews.evaluations.anonymous')

  const participant = {
    fullName: reviewerName,
    avatar: reviewerAccess?.avatar ? buildUrl(reviewerAccess.avatar) : undefined,
    isYou: reviewerAccess?.current ?? false,
  }

  return (
    <EvaluationCardBase
      participant={participant}
      evaluationType={evaluationType}
      status={status}
      publishedAt={publishedAt}
      republishedAt={republishedAt}
      endsAt={performanceReviewProcess?.endsAt}
      evaluationScore={showEvaluationScore ? evaluationScore?.score : undefined}
      onClick={handleCardClick}
      onRepublish={canRepublish ? handleRepublish : undefined}
      canReplaceReviewer={canReplaceReviewer}
      evaluationId={id}
      excludedAccessIds={excludedAccessIds}
    />
  )
}
